import { docTypeOf } from "@klarity/schema";
import { FileText, NotebookText } from "lucide-react";
import type { ReactNode } from "react";
import type { DocKind } from "../../lib/api/models";

export function Spinner({ label }: { label: string }) {
  return (
    <span role="status" className="inline-flex items-center gap-2 text-sm text-ink-3">
      <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-line border-t-green" aria-hidden="true" />
      {label}
    </span>
  );
}

export function CodeTag({ code }: { code: string }) {
  return <span className="kd-codetag">{code}</span>;
}

/** The page or document icon in front of a title. */
export function DocIcon({ kind, size = 16 }: { kind: DocKind; size?: number }) {
  return kind === "page" ? (
    <NotebookText size={size} className="shrink-0 text-ink-3" aria-hidden="true" />
  ) : (
    <FileText size={size} className="shrink-0 text-green" aria-hidden="true" />
  );
}

/** A document's code as quiet text: the name people use in links and tickets (document types spec §4.1). */
export function CodeText({ code }: { code: string }) {
  return <span className="shrink-0 font-mono text-xs whitespace-nowrap text-ink-3">{code}</span>;
}

/** The small green label: what kind of document it is, which tier a client is on. */
export function Chip({ children, title }: { children: ReactNode; title?: string }) {
  return (
    <span className="kd-typechip" title={title}>
      {children}
    </span>
  );
}

/** What kind of document it is, from the code's prefix. A wiki page and a code without a type get no chip. */
export function TypeChip({ code, kind }: { code: string | null | undefined; kind?: DocKind }) {
  const type = kind === "page" ? null : docTypeOf(code);
  if (!type) return null;
  return <Chip title={code ?? undefined}>{type.chip}</Chip>;
}

/** The tail of a document row: the code, then the type. */
export function DocMarks({ code, kind }: { code: string | null | undefined; kind?: DocKind }) {
  return (
    <>
      {code ? <CodeText code={code} /> : null}
      <TypeChip code={code} kind={kind} />
    </>
  );
}

export function EmptyState({ title, body, action }: { title: string; body?: ReactNode; action?: ReactNode }) {
  return (
    <div className="mx-auto max-w-md rounded-xl border border-dashed border-line-strong bg-surface/60 px-6 py-10 text-center">
      <p className="font-semibold text-2xl text-ink">{title}</p>
      {body ? <p className="mt-2 text-ink-2">{body}</p> : null}
      {action ? <div className="mt-5 flex justify-center">{action}</div> : null}
    </div>
  );
}

/**
 * A page's title. `aside` sits at its right, on the title's baseline: quiet text, or the page's actions as small
 * default buttons, never a primary one (page frame spec §4.2). It wraps under the title when they do not fit.
 */
export function PageTitle({
  children,
  eyebrow,
  aside,
}: {
  children: ReactNode;
  eyebrow?: ReactNode;
  aside?: ReactNode;
}) {
  const title = <h1 className="font-semibold text-3xl text-ink">{children}</h1>;
  return (
    <header className="mb-6">
      {eyebrow ? <div className="mb-1 text-xs font-medium tracking-wide text-ink-3 uppercase">{eyebrow}</div> : null}
      {aside ? (
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2">
          {title}
          <div className="flex flex-wrap items-baseline gap-3 text-sm text-ink-3">{aside}</div>
        </div>
      ) : (
        title
      )}
    </header>
  );
}
